import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import moment from 'moment';
// @mui
import {
  Grid,
  Container,
  Typography,
  Stack,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  InputLabel,
  IconButton,
  useMediaQuery,
  Backdrop,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import api from 'src/services/BaseService';
import { toast } from 'react-toastify';
import ClearButton from 'src/components/shared/ClearButton';
import Services from 'src/services/Services';
import { useLocalization } from 'src/context/LocalizationContext';
import PostDetailModal from './Views/PostDetailModal';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { maskFullNameForShopify } from 'src/utils/maskUtils';
import {
  canReturnPost,
  canSetInBranchPost,
  canTakePost,
  canDeliverSuratKargoPost,
  getCarrierLabel,
  getPreferredPostFromResults,
  getStatusLabel,
} from 'src/utils/postFlowRules';
import { getOtpTypeForPost } from 'src/utils/postOtpType';

// Güncelleme notları mock datası
const mockUpdates = [
  {
    id: '1',
    version: '2.9.3',
    title: 'Versiyon 2.9.3 ile Gelen Yenilikler',
    shortDesc: 'Versiyon 2.9.3 ile gelen yenilikler ve düzeltmeler',
    longDesc: [         
      "Aras Kargo gönderileri güncellendi.",
      "Dolap Yönetiminde kupon oluşturma akışı güncellendi."     
    ],
    date: '05.11.2026',
  },
];

// En güncel versiyonu bulan fonksiyon
function getLatestUpdate(updates) {
  return updates.reduce((latest, current) => {
    const latestParts = latest.version.split('.').map(Number);
    const currentParts = current.version.split('.').map(Number);
    for (let i = 0; i < Math.max(latestParts.length, currentParts.length); i++) {
      const l = latestParts[i] || 0;
      const c = currentParts[i] || 0;
      if (c > l) return current;
      if (c < l) return latest;
    }
    return latest;
  }, updates[0]);
}

export default function DashboardAppPage() {
  const { language } = useLocalization();
  const [user, setUser] = useState('');
  const [token, setToken] = useState('');
  const [barcode, setBarcode] = useState('');
  const [postList, setPostList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState('');
  const [forceUpdate, setForceUpdate] = useState(0);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedEasyPoints, setSelectedEasyPoints] = useState(null);

  const [pending, setPending] = useState(0);
  const [branch, setBranch] = useState(0);
  const [completed, setCompleted] = useState(0);
  const [returns, setReturns] = useState(0);
  const [loadingPending, setLoadingPending] = useState(true);
  const [loadingBranch, setLoadingBranch] = useState(true);
  const [loadingReturns, setLoadingReturns] = useState(true);
  const [loadingCompleted, setLoadingCompleted] = useState(true);
  const isMobile = useMediaQuery('(max-width:768px)');
  const [easyPointOptions, setEasyPointOptions] = useState([]);

  const [OTP, setOTP] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [selectedPost, setSelectedPost] = useState('');
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [showArasTakeConfirmModal, setShowArasTakeConfirmModal] =
    useState(false);

  const navigate = useNavigate();
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const updateVersion = getLatestUpdate(mockUpdates).version;

  function setCookie(name, value, hours) {
    const d = new Date();
    d.setTime(d.getTime() + (hours * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/`;
  }

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }


  useEffect(() => {
    const closed = getCookie('updateBannerClosed');
    if (closed !== updateVersion) {
      setShowUpdateBanner(true);
    }
  }, []);

  // Banner'ı kapatınca localStorage'a yaz
  const handleCloseBanner = () => {
    setCookie('updateBannerClosed', updateVersion, 2); // 2 saat
    setShowUpdateBanner(false);
  };

  const handleRedirectToUpdate = () => {
    setCookie('updateBannerClosed', updateVersion, 24 * 365 * 10); // 10 yıl
    setShowUpdateBanner(false);
    navigate('/dashboard/update-reports/1');
  };

  const fetchStatistics = async (
    _token,
    statusArray,
    setCount,
    setLoading,
    poiID = null,
    clear = ''
  ) => {
    setLoading(true);
    let _startDate = startDate;
    let _endDate = endDate;
    const today = new Date();
    let dateTimeType = 'created';
    if (setCount === setCompleted) {
      _endDate = _startDate = moment(today).format('YYYY-MM-DD');
      dateTimeType = 'completed';
    }
    try {
      const res = await Services.GetStatistics(_token, {
        status: statusArray,
        poiID:
          poiID ||
          (userType === 'restricted' ? user?.firstResponsibleOffice?.id : null),
        startDate:
          clear === 'clear'
            ? null
            : _startDate
              ? moment(_startDate).format('YYYY-MM-DD')
              : null,
        endDate:
          clear === 'clear'
            ? null
            : _endDate
              ? moment(_endDate).format('YYYY-MM-DD')
              : null,
        dateTimeType,
      });
      if (res.data.status) {
        setCount(res.data.result);
      } else {
        setCount(0);
      }
    } catch (err) {
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTake = (item) => {
    setOpenModal(true);
    setActionType('take');
    setSelectedPost(item);
  };

  const handleOpenReturn = (item) => {
    setOpenModal(true);
    setActionType('return');
    setSelectedPost(item);
  };

  const handleOpenComplete = (item) => {
    setOpenModal(true);
    setActionType('deliver');
    setSelectedPost(item);
  };

  const handleOpenSetInBranch = (item) => {
    setOpenModal(true);
    setActionType('setInBranch');
    setSelectedPost(item);
  };

  const handleCompleteSetInBranch = () => {
    setBackdropOpen(true);
    setOpenModal(false);
    Services.SetInBranchFromOnPorter(token, selectedPost?.barcode)
      .then((res) => {
        console.log('Zimmete Al Res:', res.data);
        if (res.data.status) {
          toast.success(
            `"${selectedPost?.barcode}" numaralı işlem başarıyla zimmete alınmıştır.`
          );
          let newPostList = postList.filter((x) => x.id !== selectedPost?.id);
          getPostForRefresh(newPostList, selectedPost?.barcode);
        } else {
          toast.error(
            `"${selectedPost?.barcode}" numaralı işlem zimmete alınırken bir sorunla karşılaşıldı.`
          );
        }
      })
      .catch((err) => {
        toast.error(
          `"${selectedPost?.barcode}" numaralı işlem zimmete alınırken bir sorunla karşılaşıldı.`
        );
        setLoading(false);
      })
      .finally(() => {
        setBackdropOpen(false);
      });
  };

  const setInBranch = () => {
    setBackdropOpen(true);
    setOpenModal(false);
    const setInBranchRequest =
      selectedPost?.apiUserType === 'shopify' &&
      selectedPost?.status === '3' &&
      selectedPost?.isDropOff === '1'
        ? Services.ChangeShopifyPostStatusToWillReturn(token, selectedPost?.id)
        : Services.SetInBranch(token, selectedPost?.id);

    setInBranchRequest
      .then((res) => {
        if (res.data.status) {
          toast.success('Başarıyla teslim alındı.');
          let newPostList = postList.filter((x) => x.id !== selectedPost?.id);
          getPostForRefresh(newPostList, selectedPost?.barcode);
        } else {
          toast.error('Teslim alınırken bir hata oluştu.');
        }
      })
      .catch((err) => {
        toast.error('Teslim alınırken bir hata oluştu.');
        setLoading(false);
      })
      .finally(() => {
        setBackdropOpen(false);
      });
  };

  const postReturn = () => {
    setBackdropOpen(true);
    setOpenModal(false);
    const returnRequest =
      selectedPost?.apiUserType === 'shopify'
        ? Services.ReturnShopifyPost(token, selectedPost?.id)
        : Services.PostReturn(token, selectedPost.barcode);

    returnRequest
      .then((res) => {
        if (res.data.status) {
          toast.success('Başarıyla iade edildi.');
          let newPostList = postList.filter((x) => x.id !== selectedPost.id);
          getPostForRefresh(newPostList, selectedPost?.barcode);
        } else {
          toast.error('İade edilirken bir hata oluştu.');
        }
      })
      .catch((err) => {
        toast.error('İade edilirken bir hata oluştu.');
      })
      .finally(() => {
        setBackdropOpen(false);
      });
  };

  const postComplete = () => {
    setBackdropOpen(true);
    const otpType = getOtpTypeForPost(selectedPost, OTP);
    Services.PostComplete(token, selectedPost.barcode, otpType, OTP)
      .then((res) => {
        if (res.data.status) {
          toast.success('Başarıyla teslim edildi.');
          let newPostList = postList.filter((x) => x.id !== selectedPost.id);
          getPostForRefresh(newPostList, selectedPost?.barcode);
          setBackdropOpen(false);
          setOpenModal(false);
        } else {
          if (res.data.message === 'Delivery otp code  not match') {
            toast.error('Teslimat kodu eşleşmiyor.');
            setBackdropOpen(false);
          } else {
            toast.error('Teslim edilirken bir hata oluştu.');
            setBackdropOpen(false);
            setOpenModal(false);
          }
        }
      })
      .catch((err) => {
        toast.error('Teslim edilirken bir hata oluştu.');
        setBackdropOpen(false);
        setOpenModal(false);
      });
  };

  useEffect(() => {
    const parsedUser = JSON.parse(localStorage.getItem('user'));
    setUser(parsedUser);
    setToken(parsedUser.token);

    let poiID = null;

    if (
      ['highadmin', 'superadmin'].includes(parsedUser?.type) ||
      (parsedUser?.type === 'user' &&
        parsedUser?.frontAuth &&
        parsedUser?.frontAuth?.includes('manager')) ||
      (parsedUser?.type === 'admin' &&
        (parsedUser?.frontAuth?.includes('EP') ||
          parsedUser?.frontAuth?.includes('EPS')))
    ) {
      setUserType('admins');
      Services.GetPoints(parsedUser.token).then((response) => {
        if (response.data.status) {
          setEasyPointOptions(response.data.result);
          setSelectedEasyPoints(null);
          // Admin kullanıcılar için poiID null olarak gönderilir
          fetchStatistics(
            parsedUser.token,
            [3],
            setPending,
            setLoadingPending,
            null
          );
          fetchStatistics(
            parsedUser.token,
            [5],
            setBranch,
            setLoadingBranch,
            null
          );
          fetchStatistics(
            parsedUser.token,
            [16, 21],
            setReturns,
            setLoadingReturns,
            null
          );
          fetchStatistics(
            parsedUser.token,
            [1],
            setCompleted,
            setLoadingCompleted,
            null
          );
        }
      });
    } else if (
      parsedUser.allowedPoints &&
      Object.keys(parsedUser.allowedPoints).length > 1
    ) {
      setUserType('limiteds');
      const options = [];
      Object.values(parsedUser.allowedPoints).forEach((office) => {
        office.centers.forEach((center) => {
          options.push({ id: center.id, title: center.title });
        });
      });
      setEasyPointOptions(options);
      const poiIDs = options.map((option) => option.id);
      setSelectedEasyPoints(poiIDs);
      // Kısıtlı kullanıcılar için poiID liste olarak gönderilir
      fetchStatistics(
        parsedUser.token,
        [3],
        setPending,
        setLoadingPending,
        poiIDs
      );
      fetchStatistics(
        parsedUser.token,
        [5],
        setBranch,
        setLoadingBranch,
        poiIDs
      );
      fetchStatistics(
        parsedUser.token,
        [16, 21],
        setReturns,
        setLoadingReturns,
        poiIDs
      );
      fetchStatistics(
        parsedUser.token,
        [1],
        setCompleted,
        setLoadingCompleted,
        poiIDs
      );
    } else {
      setUserType('restricted');
      const office = parsedUser.firstResponsibleOffice;
      setEasyPointOptions([{ id: office.id, title: office.title }]);
      setSelectedEasyPoints([office.id]);
      poiID = [office.id];
      // Yetkisiz kullanıcılar için tek bir poiID gönderilir
      fetchStatistics(
        parsedUser.token,
        [3],
        setPending,
        setLoadingPending,
        poiID
      );
      fetchStatistics(
        parsedUser.token,
        [5],
        setBranch,
        setLoadingBranch,
        poiID
      );
      fetchStatistics(
        parsedUser.token,
        [16, 21],
        setReturns,
        setLoadingReturns,
        poiID
      );
      fetchStatistics(
        parsedUser.token,
        [1],
        setCompleted,
        setLoadingCompleted,
        poiID
      );
    }
  }, []); // Boş bağımlılık dizisi ile sadece bir kez çalışır

  const handleClear = async () => {
    setStartDate(null);
    setEndDate(null);

    let poiID = null;

    if (userType === 'admins') {
      setSelectedEasyPoints(null);
      poiID = null;
    } else if (userType === 'limiteds') {
      const officeIds = user?.allowedPoints
        ? Object.values(user.allowedPoints).flatMap((office) =>
          office.centers.map((center) => center.id)
        )
        : [];

      setSelectedEasyPoints(officeIds);
      poiID =
        officeIds.length > 0 ? officeIds : [user?.firstResponsibleOffice?.id];
    } else if (userType === 'restricted') {
      const officeId = user?.firstResponsibleOffice?.id;
      setSelectedEasyPoints([officeId]);
      poiID = [officeId];
    }

    setForceUpdate((prev) => prev + 1);

    // İlk temizlemeden sonra hemen verileri çek
    fetchStatistics(token, [3], setPending, setLoadingPending, poiID, 'clear');
    fetchStatistics(token, [5], setBranch, setLoadingBranch, poiID, 'clear');
    fetchStatistics(
      token,
      [16, 21],
      setReturns,
      setLoadingReturns,
      poiID,
      'clear'
    );
    fetchStatistics(
      token,
      [1],
      setCompleted,
      setLoadingCompleted,
      poiID,
      'clear'
    );
  };

  const handleFilter = () => {
    fetchStatistics(
      token,
      [3],
      setPending,
      setLoadingPending,
      selectedEasyPoints
    );
    fetchStatistics(
      token,
      [5],
      setBranch,
      setLoadingBranch,
      selectedEasyPoints
    );
    fetchStatistics(
      token,
      [16, 21],
      setReturns,
      setLoadingReturns,
      selectedEasyPoints
    );
    fetchStatistics(
      token,
      [1],
      setCompleted,
      setLoadingCompleted,
      selectedEasyPoints
    );
  };

  const getPostForRefresh = (newPostList, barcode) => {
    setLoading(true);
    api
      .post('flow/get-posts', { barcode })
      .then((res) => {
        if (res.data.status) {
          const preferredPost = getPreferredPostFromResults(res.data.result);
          if (!preferredPost) {
            toast.error('Gönderi bulunamadı.', {
              position: 'top-center',
              autoClose: 5000,
            });
            return;
          }
          newPostList.push(preferredPost);
          setPostList(newPostList);
        } else {
          toast.error('Gönderi çekilirken ir hata oluştu.', {
            position: 'top-center',
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        toast.error('Gönderi bulunamadı.', {
          position: 'top-center',
          autoClose: 5000,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const validateBarcodeForAdd = () => {
    if (barcode?.length < 5) {
      toast.warning('Geçerli bir barkod giriniz.', {
        position: 'top-center',
        autoClose: 5000,
      });
      setBarcode('');
      return false;
    } else if (postList.some((item) => item?.barcode === barcode)) {
      toast.warning('Girdiğiniz barkod aşağıdaki listenizde bulunmaktadır.', {
        position: 'top-center',
        autoClose: 5000,
      });
      setBarcode('');
      return false;
    }

    return true;
  };

  const getPost = () => {
    if (!validateBarcodeForAdd()) return;

    setLoading(true);
    api
      .post('flow/get-posts', { barcode })
      .then((res) => {
        if (res.data.status) {
          const preferredPost = getPreferredPostFromResults(res.data.result);
          if (!preferredPost) {
            toast.error('Gönderi bulunamadı.', {
              position: 'top-center',
              autoClose: 5000,
            });
            return;
          }
          setPostList((prevList) => [...prevList, preferredPost]);
          // window.open(
          //   `/dashboard/post-details/${res.data.result[0]?.barcode}`,
          //   '_blank'
          // );
        } else {
          if (res.data.message === 'You are not post owner') {
            toast.error(
              'Gönderi farklı bir şubeye aittir, yöneticinizle iletişime geçiniz.',
              {
                position: 'top-center',
                autoClose: 5000,
              }
            );
          } else {
            if (
              (barcode?.length === 8 && /^[a-z]{2}/i.test(barcode)) ||
              barcode?.length === 34
            ) {
              toast.error(res.data.message, {
                position: 'top-center',
                autoClose: 5000,
              });
            } else {
              toast.error('Gönderi bulunamadı.', {
                position: 'top-center',
                autoClose: 5000,
              });
            }
          }
        }
      })
      .catch(() => {
        toast.error('Gönderi bulunamadı.', {
          position: 'top-center',
          autoClose: 5000,
        });
      })
      .finally(() => {
        setLoading(false);
        setBarcode('');
      });
  };

  const searchPostInDb = () => {
    if (!validateBarcodeForAdd()) return;

    setLoading(true);
    api
      .post('flow/post-search-barcode-in-db', { barcode })
      .then((res) => {
        if (res.data.status) {
          const preferredPost = getPreferredPostFromResults(res.data.result);
          if (!preferredPost) {
            toast.error('Gönderi bulunamadı.', {
              position: 'top-center',
              autoClose: 5000,
            });
            return;
          }
          setPostList((prevList) => [...prevList, preferredPost]);
        } else {
          toast.error(res.data.message || 'Gönderi bulunamadı.', {
            position: 'top-center',
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        toast.error('Gönderi bulunamadı.', {
          position: 'top-center',
          autoClose: 5000,
        });
      })
      .finally(() => {
        setLoading(false);
        setBarcode('');
      });
  };

  const handleOpenArasTakeConfirm = () => {
    if (!validateBarcodeForAdd()) return;
    setShowArasTakeConfirmModal(true);
  };

  const handleConfirmArasTake = () => {
    setShowArasTakeConfirmModal(false);
    getPost();
  };

  const removePost = (barcodeToRemove) => {
    setPostList((prevList) =>
      prevList.filter((item) => item.barcode !== barcodeToRemove)
    );
  };

  // Güncellenmiş BoxItem Bileşeni
  const BoxItem = ({ label, count, loading, tooltipTitle, tooltipText, placement = 'right-start' }) => (
    <Grid
      container
      alignItems="center"
      style={{
        padding: '16px',
        backgroundColor: 'rgba(38, 39, 41, 0.8)',
        borderRadius: '8px',
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      {/* Özelleştirilmiş Tooltip */}
      <Tooltip
        title={
          <Stack spacing={0.5} p={0.5}>
            <Typography
              variant="subtitle2"
              sx={{
                color: 'rgba(52, 64, 84, 1)',
                fontWeight: 700,
                fontSize: '13px',
                lineHeight: '18px'
              }}
            >
              {tooltipTitle}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(71, 84, 103, 1)',
                fontSize: '12px',
                lineHeight: '18px'
              }}
            >
              {tooltipText}
            </Typography>
          </Stack>
        }
        
        placement={placement}
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: '#ffffff',
              boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
              borderRadius: '8px',
              padding: '12px',
              border: '1px solid #EAECF0',
              maxWidth: 326
            }
          },
          arrow: {
            sx: {
              color: '#ffffff',
            }
          }
        }}
      >
        <HelpOutlineIcon
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'rgba(255, 255, 255, 0.7)',
            cursor: 'pointer',
            fontSize: '20px'
          }}
        />
      </Tooltip>

      <Typography
        style={{ textAlign: 'center', width: '100%', color: 'white' }}
      >
        {label} <br />
        {loading ? (
          <CircularProgress
            sx={{ marginTop: '5px', color: 'white' }}
            size={24}
          />
        ) : (
          <strong style={{ fontSize: 24 }}>
            {count} {language.commons.quantity}
          </strong>
        )}
      </Typography>
    </Grid>
  );

  return (
    <>
      <Helmet>
        <title>{language.dashboard_app_page.titles.site_title} </title>
      </Helmet>

      <Backdrop sx={{ color: '#fff', zIndex: 2 }} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="xl">

        {/* Güncelleme Bannerı */}
        {showUpdateBanner && (
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={2}
            sx={{
              background: '#fff',
              borderRadius: 2,
              boxShadow: 1,
              p: 2,
              mb: 3,
              position: 'relative',
            }}
          >
            <Stack sx={{ alignItems: 'center', justifyContent: 'center', width: 36, height: 36, backgroundColor: '#FEE1E1', borderRadius: 1 }}>
              <Icon icon="mdi:refresh" color="#DB3831" width={24} height={24} />
            </Stack>
            <Stack flex={1}>
              <Typography fontWeight={700} color="#111928" sx={{ mb: 0.5 }}>
                {language.dashboard_app_page.titles.versionUpdateTitle}
              </Typography>
              <Typography variant="body2" color="#4B5563">
                {/* Dil Eklenirken Versiyon numarası {{version}} şeklinde yazılmalı.  */}
                {language.dashboard_app_page.titles.versionUpdated.replace('{{version}}', updateVersion)}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="error"
                  sx={{ minWidth: 110, fontSize: 12, py: 1, backgroundColor: '#DB3831' }}
                  onClick={handleRedirectToUpdate}
                >
                  {language.dashboard_app_page.titles.checkNote}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  sx={{ minWidth: 110, py: 1, borderColor: '#d1d5db', color: '#374151', fontSize: 12 }}
                  onClick={handleCloseBanner}
                >
                  {language.dashboard_app_page.titles.notNow}
                </Button>
              </Stack>
            </Stack>
            <IconButton
              onClick={handleCloseBanner}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        )}
        {isMobile}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={2}
          mb={5}
          alignItems={'center'}
        >
          <FormControl sx={isMobile ? { width: '100%' } : null}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                format="DD/MM/YYYY"
                label={language.commons.labels.start_date}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
              />
            </LocalizationProvider>
          </FormControl>
          <FormControl sx={isMobile ? { width: '100%' } : null}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                format="DD/MM/YYYY"
                label={language.commons.labels.end_date}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
              />
            </LocalizationProvider>
          </FormControl>
          <FormControl sx={isMobile ? { width: '100%' } : { minWidth: 200 }}>
            <InputLabel id="easy-point-label">
              {language.dashboard_app_page.titles.title}
            </InputLabel>
            <Select
              labelId="easy-point-label"
              multiple={easyPointOptions.length > 1}
              value={selectedEasyPoints || []}
              onChange={(event) => setSelectedEasyPoints(event.target.value)}
              input={
                <OutlinedInput
                  label={language.dashboard_app_page.titles.title}
                />
              }
              renderValue={(selected) => {
                if (easyPointOptions.length === 1) {
                  return easyPointOptions[0].title;
                }

                const selectedTitles = selected
                  .map(
                    (value) =>
                      easyPointOptions.find((option) => option.id === value)
                        ?.title
                  )
                  .join(', ');

                const maxLength = 30;

                return selectedTitles.length > maxLength
                  ? `${selectedTitles.substring(0, maxLength)}...`
                  : selectedTitles;
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5,
                  },
                },
              }}
              disabled={easyPointOptions.length === 1}
            >
              {easyPointOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack spacing={1} direction="row">
            <ClearButton onClick={handleClear} />
            <Button variant="contained" color="primary" onClick={handleFilter}>
              {language.commons.filter2}
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={2} mb={5}>
          <Grid item xs={12} sm={6} md={3}>
            <BoxItem
              label={language.dashboard_app_page.titles.pending_posts}
              count={pending}
              loading={loadingPending}
              tooltipTitle={language.dashboard_app_page.titles.pending_posts}
              tooltipText="Siparişi oluşturulmuş tüm gönderiler bu durumda listelenir."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <BoxItem
              label={language.dashboard_app_page.titles.branch_pasts}
              count={branch}
              loading={loadingBranch}
              tooltipTitle={language.dashboard_app_page.titles.branch_pasts}
              tooltipText="Noktanın anlık zimmetinde bulunan tüm gönderiler bu durumda listelenir."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <BoxItem
              label={language.dashboard_app_page.titles.refund}
              count={returns}
              loading={loadingReturns}
              tooltipTitle={language.dashboard_app_page.titles.refund}
              tooltipText="Nokta tarafından kargo firmasına iade edilen gönderiler bu durumda listelenir."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <BoxItem
              label={language.dashboard_app_page.titles.delivery}
              count={completed}
              loading={loadingCompleted}
              tooltipTitle={language.dashboard_app_page.titles.delivery}
              tooltipText="Nokta tarafından gün içinde alıcıya ulaştırılan tüm gönderiler bu durumda listelenir."
              placement="top"
            />
          </Grid>
        </Grid>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          mb={5}
        >
          <TextField
            id="barcode"
            name="barcode"
            type="text"
            label={language.commons.enter_barcode}
            autoComplete="off"
            fullWidth
            value={barcode}
            style={{ backgroundColor: 'white' }}
            onChange={(e) =>
              setBarcode(e.target.value.trim().toLocaleUpperCase('tr'))
            }
          />
          <Button
            style={{ marginLeft: 10 }}
            variant="contained"
            color="primary"
            size="large"
            onClick={searchPostInDb}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : language.commons.search}
          </Button>
          <Button
            style={{ marginLeft: 10 }}
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleOpenArasTakeConfirm}
            disabled={loading}
            sx={{width: '100%', maxWidth: '143px', fontSize: '14px'}}
          >
            Aras Paketi Al
          </Button>
        </Stack>

        <Dialog
          open={showArasTakeConfirmModal}
          onClose={() => setShowArasTakeConfirmModal(false)}
        >
          <DialogTitle>Aras Paketi Al</DialogTitle>
          <DialogContent>
            <Typography>
              {barcode} Barkod numaralı Aras Kargo gönderisi zimmete alınacaktır.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowArasTakeConfirmModal(false)}>
              İptal Et
            </Button>
            <Button
              onClick={handleConfirmArasTake}
              variant="contained"
              color="primary"
            >
              Devam Et
            </Button>
          </DialogActions>
        </Dialog>

        {postList?.map((item, index) => (
          <Grid
            container
            spacing={2}
            alignItems="center"
            key={index}
            style={{
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography>
              {`${index + 1} - ${item?.id}: `}
              <br />
              <strong
                onClick={() => {
                  window.open(
                    `/dashboard/post-details/${item?.barcode}`,
                    '_blank'
                  );
                }}
                style={{ cursor: 'pointer' }}
              >
                {`${getCarrierLabel(item)} - ${
                  item?.apiUserType === 'shopify'
                    ? maskFullNameForShopify(item?.willTakenBy)
                    : item?.willTakenBy || '-'
                } - ${item?.barcode} - (${getStatusLabel(item)})`}
              </strong>
            </Typography>
            <div>
              {canTakePost(item) && (
                <Button onClick={() => handleOpenTake(item)}>Teslim Al</Button>
              )}

              {canDeliverSuratKargoPost(item) && (
                <Button onClick={() => handleOpenComplete(item)}>Teslim Et</Button>
              )}

              {canSetInBranchPost(item) && (
                <Button onClick={() => handleOpenSetInBranch(item)}>
                  {item?.apiUserType === 'shopify' ? 'Teslim Al' : 'Zimmete Al'}
                </Button>
              )}

              {canReturnPost(item) && item?.apiUserType === 'shopify' && (
                <Button onClick={() => handleOpenReturn(item)}>İade Et</Button>
              )}

              {(item?.apiUserType === 'surat-kargo' &&
                String(item?.status) === '47') ||
              (item?.status === '3' ||
                item?.status === '4' ||
                item?.status === '5' ||
                (item?.status === '9' && item?.apiUserType !== 'trendyol')) &&
                item?.apiUserType !== 'aras-kargo' ? (
                <></>
              ) : (
                <IconButton
                  onClick={() => removePost(item.barcode)}
                  style={{ color: 'red' }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </div>
          </Grid>
        ))}
      </Container>

      {actionType === 'take' && (
        <PostDetailModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onConfirm={setInBranch}
          type="take"
          post={selectedPost}
        />
      )}
      {actionType === 'return' && (
        <PostDetailModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onConfirm={postReturn}
          type="return"
          post={selectedPost}
        />
      )}
      {actionType === 'setInBranch' && (
        <PostDetailModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onConfirm={handleCompleteSetInBranch}
          type="setInBranch"
          post={selectedPost}
        />
      )}
      {actionType === 'deliver' && (
        <PostDetailModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onConfirm={postComplete}
          type="deliver"
          setOTP={setOTP}
          OTP={OTP}
          post={selectedPost}
        />
      )}
    </>
  );
}
